import axios from 'axios';
import dayjs from 'dayjs';
import ja from 'dayjs/locale/ja';
import { CUSTOMER_API_URL } from '../constants';
import { useEffect, useState } from 'react';
import { Setting } from '../App';
import { SubscriptionType, Shop } from '../types/api/subscriptions-api-type';

dayjs.locale(ja);

export const useSubscriptionSummary = (
  setting: Setting,
  subscription: SubscriptionType | undefined
) => {

  const [subscriptionDetail, setSubscriptionDetail] = useState<SubscriptionDetail>();
  const [summary, setSummary] = useState<Summary>();

  useEffect(() => {
    if (!setting.token) return;
    if (!subscription) return;

    (async () => {
      try {
        const response = await axios.post(
          `${CUSTOMER_API_URL}?action=getSubscription`,
          {
            subscriptionId: subscription.subscriptionContractId
          },
          { headers: { Authorization: setting.token } }
        );
        const detail = response.data as SubscriptionDetail
        const summary = createSummary(detail);

        setSubscriptionDetail(detail);
        setSummary(summary);

      } catch (error) {
        console.log(error);
      }
    })();
  }, [setting.token, subscription]);

  return { summary, subscriptionDetail };
}

const createSummary = (subscription: SubscriptionDetail) => {
  const modifyLimitDate = 
  dayjs(subscription.nextBillingDate)
    .subtract(subscription.shop.allowModifyDays || 0, 'day');

  const nextBillingDate = dayjs(subscription.nextBillingDate);

  const deliveryDays = subscription.deliveryDays === -1
    ? subscription.shop.deliveryDays
    : subscription.deliveryDays;
  const disableDeliveryWeeks = createDisableDeliveryWeeks(subscription);
  const nextDeliveryDate = createNextDeliveryDate(nextBillingDate, deliveryDays, disableDeliveryWeeks);

  let nextDeliveryMaxDate: (dayjs.Dayjs | undefined) = nextBillingDate.add(subscription.shop.deliveryDaysMax || 30, 'day');
  // 「お届け日に合わせて注文日を変更する」チェックが入っている場合
  if (subscription.shop.changeNextBillingDateWithDeliveryDate) {
    const nextDeliveryLimitDate = createNextDeliveryDate(
      nextBillingDate, subscription.shop.deliveryDaysMax, disableDeliveryWeeks
    );
    const nextBillingLimitDate = nextBillingDate.add(
      subscription.billingPolicyIntervalCount,
      subscription.billingPolicyInterval.toLocaleLowerCase() as 'day' | 'week' | 'month'
    );
    nextDeliveryMaxDate = 
      subscription.shop.deliveryDaysMax === 0
        ? undefined
        : nextBillingLimitDate.isAfter(nextDeliveryLimitDate)
          ? nextDeliveryLimitDate : nextBillingLimitDate;
  }

  let nextDeliveryMinDate = nextBillingDate.add(subscription.shop.deliveryDays, 'day');
  // 「お届け日に合わせて注文日を変更する」チェックが入っている場合
  if (subscription.shop.changeNextBillingDateWithDeliveryDate) {
    const purchasedDate = dayjs(
      subscription.billingAttempts.length
        ? subscription.billingAttempts[0].createdAt
        : subscription.createdAt
    );
    nextDeliveryMinDate =
      dayjs()
        .add(subscription.shop.deliveryDays, 'day')
        .isAfter(purchasedDate.add(subscription.shop.deliveryDays, 'day'))
          // 最短の日時に変更しようとすると、変更締切日の時間が過ぎていると選択できない
          // そのためお届け日時指定の最短日時に+1をしている
          ? dayjs().add(subscription.shop.deliveryDays + 1, 'day')
          : purchasedDate.add(subscription.shop.deliveryDays, 'day');
  }

  const enableDeliveryTimes = createEnabledDeliveryTimes(subscription);
  const deliveryTime = subscription.deliveryTime || 'AM';

  const totalPrice =
    subscription.deliveryPriceAmount +
    subscription.subscriptionLines.reduce(
      (sum, subscriptionLine) => sum + subscriptionLine.currentPriceAmount * subscriptionLine.quantity,
      0
    );
  const totalOriginalPrice =
    subscription.deliveryPriceAmount +
    subscription.subscriptionLines.reduce(
      (sum, subscriptionLine) => sum + subscriptionLine.lineDiscountedPriceAmount * subscriptionLine.quantity,
      0
    );
  const deliveryPrice = subscription.deliveryPriceAmount
  
  return {
    modifyLimitDate: modifyLimitDate.toDate(),
    nextBillingDate: nextBillingDate.toDate(),
    nextDeliveryDate: nextDeliveryDate.toDate(),
    nextDeliveryMinDate: nextDeliveryMinDate.toDate(),
    nextDeliveryMaxDate: nextDeliveryMaxDate ? nextDeliveryMaxDate.toDate() : undefined,
    disableDeliveryWeeks,
    enableDeliveryTimes,
    deliveryTime,
    totalPrice,
    totalOriginalPrice,
    deliveryPrice,
  };
}

const createNextDeliveryDate = (
  nextBillingDate: dayjs.Dayjs,
  deliveryDays: number,
  disableDeliveryWeeks: { value: string, disabled: boolean }[]
): dayjs.Dayjs => {

  let result = dayjs(nextBillingDate).add(deliveryDays, 'day');

  if (!disableDeliveryWeeks.every((item) => item.disabled)) {
    const index = result.day();
    const _first = disableDeliveryWeeks.slice(index);
    const _rest = disableDeliveryWeeks.slice(0, index);
    const disableDays = _first.concat(_rest);
    const add = disableDays.findIndex((item) => !item.disabled);
    result = result.add(add, 'days');
  }

  return result;
}

const createDisableDeliveryWeeks = (subscription: SubscriptionDetail) => {
  return [
    { value: "Sun", disabled: subscription.shop.disableDeliveryDaysSun },
    { value: "Mon", disabled: subscription.shop.disableDeliveryDaysMon },
    { value: "Tue", disabled: subscription.shop.disableDeliveryDaysTue },
    { value: "Wed", disabled: subscription.shop.disableDeliveryDaysWed },
    { value: "Thr", disabled: subscription.shop.disableDeliveryDaysThr },
    { value: "Fri", disabled: subscription.shop.disableDeliveryDaysFri },
    { value: "Sat", disabled: subscription.shop.disableDeliveryDaysSat },
  ]
}

const createEnabledDeliveryTimes = (subscription: SubscriptionDetail) => {
  const selectedDeliveryTime = 
    subscription.deliveryTime ? subscription.deliveryTime : 'AM';

  // 管理画面設定値がsubscription.shop.deliveryTimesに含まれているので
  // そちらを利用して表示用の配列を作成、それを返す
  return subscription.shop.deliveryTimes.map(time => {
    return {
      label: time.title,
      value: time.tag,
      selected: selectedDeliveryTime === time.tag
    }
  });
}

// types

export type SubscriptionDetail = SubscriptionType & {
  customerPaymentMethodId: string;
  deliveryTimeText: string;
  paymentType: string;
  shop: ShopDetail;
  //subscriptionHistories: SubscriptionHistory[];
  //subscriptionLines: SubscriptionLineDetail[];
  //billingAttempts: BillingAttemptDetail[];
}

type ShopDetail = Shop & {
  showDelivery: boolean;
  mypageChangeDeliveryDays: boolean;
  showChangeProduct: boolean;
  showNewAddProduct: boolean;
  showChangePayment: boolean;
  showRemoveProduct: boolean;
  showNewOrder: boolean;
  showChangeQuantity: boolean;
  maxSkipCount: number;
  hideLowestBuySkip: boolean;
  hideLowestBuyPause: boolean;
  hideLowestBuyCancel: boolean;
  showPlanModify: boolean;
  isAllowModifyDays: boolean;
  allowModifyDays: number;
  hideLowestModify: boolean;
  showStockAlert: boolean;
  deliveryDaysMax: number;
  changeNextBillingDateWithDeliveryDate: boolean;
  enableDeliveryDateNoDay: boolean;
  deliveryTimes: DeliveryTime[];
}

type DeliveryTime = {
  tag: string;
  title: string;
 }

export type Summary = {
  modifyLimitDate: Date,
  nextBillingDate: Date; //
  nextDeliveryDate: Date,
  nextDeliveryMinDate: Date,
  nextDeliveryMaxDate?: Date,
  disableDeliveryWeeks: DisableDeliveryWeek[],
  enableDeliveryTimes: EnableDeliveryTime[] //
  deliveryTime: string;
  totalPrice: number; //
  totalOriginalPrice: number; //
  deliveryPrice: number; //
}

type DisableDeliveryWeek = {
  value: string;
  disabled: boolean;
}

type EnableDeliveryTime = {
  label: string;
  value: string;
  selected: boolean;
}
